import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type PageHeaderSlots = {
  filters?: ReactNode;
  actions?: ReactNode;
  filterCount?: number;
};

type PageHeaderContextValue = {
  slots: PageHeaderSlots;
  setSlots: (slots: PageHeaderSlots) => void;
};

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<PageHeaderSlots>({});
  const value = useMemo(() => ({ slots, setSlots }), [slots]);
  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
}

function useCtx() {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error("PageHeaderProvider missing");
  return ctx;
}

export function usePageHeaderState(): PageHeaderSlots {
  return useCtx().slots;
}

export function usePageHeader(slots: PageHeaderSlots) {
  const { setSlots } = useCtx();
  const { filters, actions, filterCount } = slots;
  const setSlotsCb = useCallback(setSlots, [setSlots]);
  useEffect(() => {
    setSlotsCb({ filters, actions, filterCount });
    return () => setSlotsCb({});
  }, [filters, actions, filterCount, setSlotsCb]);
}
